import {
  dragonTileQueryOptions,
  dragonTilesKeys,
  useUpdateDragonTile,
  framePreviewsPageQueryOptions,
  framesPageQueryOptions,
  useAssignFrames,
} from "@api/dragon-tiles";
import { FrameCardLink } from "@components/FrameCard/FrameCardLink";
import Panel from "@features/DragonTiles/components/Panel.styles";
import { Box, Typography, FormHelperText, Stack, Grid } from "@mui/material";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  useMatch,
  useNavigate,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { z } from "zod";

import { CreateFrameModal } from "../../../../features/DragonTiles/components/CreateFrameModal";

import { siteQueryOptions } from "@/api/siteDetails";
import { TilesPagination } from "@/components/TilesPagination/TilesPagination";
import { BroadcastWarmupBanner } from "@/features/BroadcastV2/components/BroadcastWarmupBanner";
import { useDragonTileDisplayName } from "@/features/Connectors/byocTileDisplayName";
import { getAudienceInnerTiles } from "@/features/DragonTiles/audienceInnerTiles";
import { isComingSoonTile } from "@/features/DragonTiles/comingSoon";
import { AnalogSequencingPanel } from "@/features/DragonTiles/components/AnalogSequencingPanel";
import { AssignTilesToolbar } from "@/features/DragonTiles/components/AssignTilesToolbar";
import { AudiencesSidebarSection } from "@/features/DragonTiles/components/AudiencesSidebarSection";
import { AudienceTileSection } from "@/features/DragonTiles/components/AudienceTileSection";
import { DragonTileActivateSwitch } from "@/features/DragonTiles/components/DragonTileActivateSwitch";
import { DragonTileConfigForm } from "@/features/DragonTiles/components/DragonTileConfigForm";
import { InnerTilesToolbar } from "@/features/DragonTiles/components/InnerTilesToolbar";
import LeftSidebar from "@/features/DragonTiles/components/LeftSidebar";
import { MoveOrCopyConfirmDialog } from "@/features/DragonTiles/components/MoveOrCopyConfirmDialog";
import { ParentTile } from "@/features/DragonTiles/components/ParentTile/ParentTile";
import { TileSection } from "@/features/DragonTiles/components/TileSection";
import { useAssignModeHandlers } from "@/features/DragonTiles/hooks/useAssignModeHandlers";
import {
  buildFramePreviewsFilters,
  useDragonTileWithFrames,
} from "@/features/DragonTiles/hooks/useDragonTileWithFrames";
import {
  buildAssignTargetOptions,
  getParentPoolInnerTile,
  mapFramesById,
  syncSelectedAudienceForInnerTile,
} from "@/features/DragonTiles/innerTileUtils";
import { parentTileDescriptionHash } from "@/features/DragonTiles/parentTileDescriptionHash";
import { parentTileDisabledHash } from "@/features/DragonTiles/parentTileDisabledHash";
import {
  resetParentTileInteractionState,
  resetParentTileView,
} from "@/features/DragonTiles/resetParentTileView";
import { shouldHideNewSubscriberListOptIn } from "@/features/DragonTiles/shouldHideNewSubscriberListOptIn";
import { ParentTileViewStore } from "@/features/DragonTiles/store/parentTileView";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";
import { getTileAttention } from "@/utils/dragonTiles";
import {
  hasElevatedAccessValue,
  usePrivilegedModeStore,
} from "@/utils/privilegedMode";

const PARENT_TILES_PAGE_SIZE = 10;

const searchSchema = z.object({
  page: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === "string" ? Number(v) : v))
    .pipe(z.number().int().positive())
    .catch(1)
    .optional(),
  search: z.string().optional(),
  medium: z.enum(["email", "sms"]).optional(),
  status: z.enum(["active", "inactive"]).optional(),
  order_by: z
    .enum(["create_date", "modify_date", "name", "is_active"])
    .optional(),
  order: z.enum(["asc", "desc"]).optional(),
  expectedRPM: z
    .union([z.string(), z.boolean()])
    .transform((v) => (typeof v === "string" ? v !== "false" : !!v))
    .optional(),
});

type ParentTileSearch = z.infer<typeof searchSchema>;

const buildFrameFilters = (search: ParentTileSearch) => ({
  ...(search.search ? { search: search.search } : {}),
  ...(search.medium ? { medium: search.medium } : {}),
  ...(search.status ? { is_active: search.status === "active" } : {}),
  ...(search.order_by ? { order_by: search.order_by } : {}),
  ...(search.order ? { order: search.order } : {}),
});

export const Route = createFileRoute("/_auth/dragon-tiles/$siteId/$parentTile")(
  {
    component: RouteComponent,
    validateSearch: zodValidator(searchSchema),
    loaderDeps: ({ search }) =>
      [search.page, buildFrameFilters(search)] as const,
    beforeLoad: async ({ context, params }) => {
      const privilegedModeDisabled = usePrivilegedModeStore.getState().disabled;
      const hasElevatedAccess = hasElevatedAccessValue(
        context.auth?.user?.user.role,
        privilegedModeDisabled,
      );
      if (hasElevatedAccess) return;

      const { queryClient } = context as { queryClient: QueryClient };
      const dragonTile = await queryClient.ensureQueryData(
        dragonTileQueryOptions(params.siteId, params.parentTile),
      );

      if (isComingSoonTile(dragonTile?.slug)) {
        throw redirect({
          to: "/dragon-tiles/$siteId",
          params: { siteId: params.siteId },
        });
      }
    },
    loader: async ({ params, context, deps: [page, frameFilters] }) => {
      const { queryClient } = context as {
        queryClient: QueryClient;
      };
      const { siteId, parentTile } = params;
      const safePage = Math.max(1, page ?? 1);

      const dragonTile = await queryClient.ensureQueryData(
        dragonTileQueryOptions(siteId, parentTile),
      );

      if (!dragonTile) {
        throw new Error(`Dragon tile "${parentTile}" not found`);
      }

      if (!dragonTile.is_clickable) {
        throw redirect({
          to: "/dragon-tiles/$siteId",
          params: { siteId },
        });
      }

      const parentPoolInnerTileId = getParentPoolInnerTile(dragonTile)?.id;
      await queryClient.ensureQueryData(
        framePreviewsPageQueryOptions(
          siteId,
          safePage,
          PARENT_TILES_PAGE_SIZE,
          buildFramePreviewsFilters(
            parentTile,
            parentPoolInnerTileId,
            frameFilters,
          ),
        ),
      );

      // Prefetch site data - required by FrameEditorWrapper's useSuspenseQuery
      await queryClient.ensureQueryData(siteQueryOptions(siteId));
    },
  },
);

function RouteComponent() {
  const { siteId, parentTile: dragonTileId } = Route.useParams();
  const getDisplayName = useDragonTileDisplayName(siteId);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/dragon-tiles/$siteId/$parentTile" });
  const queryClient = useQueryClient();
  const { mutate: updateDragonTile, isPending: isUpdatingDragonTile } =
    useUpdateDragonTile({
      optimisticActivation: true,
      suppressSuccessToast: true,
    });
  const { mutate: assignFrames, isPending: isAssigning } = useAssignFrames();
  const [createFrameRequest, setCreateFrameRequest] = React.useState<{
    innerTileId: string;
    forcedLayoutType?: "email" | "text";
  } | null>(null);
  const isAssignMode = ParentTileViewStore.useIsAssignMode();
  const isFrameRouteActive = useMatch({
    from: "/_auth/dragon-tiles/$siteId/$parentTile/tile/$frameId",
    shouldThrow: false,
  });

  const page = Math.max(1, search.page ?? 1);

  const frameFilters = React.useMemo(() => buildFrameFilters(search), [search]);

  const updateSearch = React.useCallback(
    <K extends keyof ParentTileSearch>(
      key: K,
      value: ParentTileSearch[K] | undefined,
    ) => {
      void navigate({
        search: (prev) => {
          const next = { ...prev };
          if (value === undefined || value === "") {
            delete next[key];
          } else {
            next[key] = value;
          }
          next.page = 1;
          return next;
        },
        replace: true,
      });
    },
    [navigate],
  );

  const showFiltersAndActions = !isFrameRouteActive;

  const { dragonTile, frames, lists, totalCount } = useDragonTileWithFrames(
    siteId,
    dragonTileId,
    page,
    PARENT_TILES_PAGE_SIZE,
    frameFilters,
  );

  const handleCreateTile = React.useCallback(
    (innerTileId: string, options?: { medium?: "email" | "sms" }) => {
      syncSelectedAudienceForInnerTile(dragonTile, innerTileId);
      const medium = options?.medium;
      setCreateFrameRequest({
        innerTileId,
        forcedLayoutType:
          medium === "sms" ? "text" : medium === "email" ? "email" : undefined,
      });
    },
    [dragonTile],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / PARENT_TILES_PAGE_SIZE),
  );
  const clampedPage = Math.min(page, totalPages);

  const audienceInnerTiles = React.useMemo(
    () => getAudienceInnerTiles(dragonTile),
    [dragonTile],
  );

  const selectedAudienceId = ParentTileViewStore.useSelectedAudienceId();
  const { clearSelection, enterAssignMode: enterAssignModeAction } =
    ParentTileViewStore.actions;

  const isAnalogModeActive = dragonTile.analog_sequence;

  // Reset when switching parent tiles, not when remounting after create-tile navigation.
  const prevDragonTileIdRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (
      prevDragonTileIdRef.current !== undefined &&
      prevDragonTileIdRef.current !== dragonTileId
    ) {
      resetParentTileView();
    }
    prevDragonTileIdRef.current = dragonTileId;
  }, [dragonTileId]);

  // Clear transient interaction overlays when this page unmounts, but keep the
  // sidebar audience selection: full-page create/editor routes (e.g. /new/$variant)
  // are siblings, so navigating to them unmounts this route — preserving the
  // selection lets a back navigation return to the same filtered view. A genuine
  // parent-tile switch is fully reset by the dragonTileId effect above.
  React.useEffect(() => () => resetParentTileInteractionState(), []);

  const hasSelectedAudience =
    selectedAudienceId !== null &&
    audienceInnerTiles.some((it) => it.audience_id === selectedAudienceId);
  const visibleAudienceInnerTiles = hasSelectedAudience
    ? audienceInnerTiles.filter((it) => it.audience_id === selectedAudienceId)
    : audienceInnerTiles;

  const parentPoolInnerTile = getParentPoolInnerTile(dragonTile);

  const assignSourceInnerTileId =
    ParentTileViewStore.useAssignSourceInnerTileId();

  const sourceInnerTile = assignSourceInnerTileId
    ? dragonTile.inner_tiles.find((it) => it.id === assignSourceInnerTileId)
    : undefined;
  const sourceFrameIds = sourceInnerTile?.frames.map((f) => f.id) ?? [];
  const { data: sourceFramesResponse } = useQuery({
    ...framesPageQueryOptions(siteId, 1, sourceFrameIds.length || 1, {
      dragon_tile: dragonTileId,
      ids: sourceFrameIds.join(","),
    }),
    enabled: isAssignMode && sourceFrameIds.length > 0,
  });

  const framesById = React.useMemo(
    () =>
      mapFramesById(
        frames.map(({ frame }) => frame),
        sourceFramesResponse?.results ?? [],
      ),
    [frames, sourceFramesResponse],
  );

  const handleEnterAssignMode = (sourceInnerTileId: string) => {
    enterAssignModeAction(
      sourceInnerTileId,
      buildAssignTargetOptions(
        sourceInnerTileId,
        parentPoolInnerTile,
        audienceInnerTiles,
      ),
    );
  };

  const {
    handleSaveAssign,
    handleRemoveFromAudience,
    handleConfirmMoveOrCopy,
  } = useAssignModeHandlers({
    siteId,
    dragonTileId,
    parentPoolInnerTileId: parentPoolInnerTile?.id,
    framesById,
    assignFrames,
  });

  // Helper to invalidate dragon tile queries after mutations
  const invalidateDragonTile = () => {
    void queryClient.invalidateQueries({
      queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
    });
    void queryClient.invalidateQueries({
      queryKey: dragonTilesKeys.all(siteId),
    });
  };

  const hasConfigs = !!Object.keys(dragonTile.config).length;
  const shouldShowConfig =
    hasConfigs && !parentTileDisabledHash[dragonTile.slug]?.disableConfig;
  const hideNewSubscriberListOptIn = shouldHideNewSubscriberListOptIn(
    dragonTile.slug,
  );
  const shouldShowConfigSection =
    shouldShowConfig && !hideNewSubscriberListOptIn;

  const isListIdsEmpty = (type: "inclusions" | "exclusions") =>
    shouldShowConfig &&
    !hideNewSubscriberListOptIn &&
    dragonTile.config?.[type] &&
    Array.isArray(dragonTile.config[type]?.listIds) &&
    dragonTile.config[type].listIds.length === 0;

  const inclusionsListIdsAreEmpty = isListIdsEmpty("inclusions");

  const unActivatableDueToListRequirement =
    shouldShowConfig && inclusionsListIdsAreEmpty;

  const messages = [];
  if (inclusionsListIdsAreEmpty) messages.push("Inclusion Lists are required.");

  const activateSwitchHelperText = shouldShowConfig && messages.length > 0 && (
    <FormHelperText>{messages.join(" ")}</FormHelperText>
  );

  const { status: tileStatus, message: attentionMessage } = getTileAttention(
    dragonTile,
    false,
    { framesLoaded: true },
  );

  const parentPoolSection = (
    <TileSection
      testId="parent-tiles-section"
      title="Parent tiles"
      totalCount={totalCount}
      description="Tiles not assigned to an audience are shown to all eligible subscribers"
      assignModeActive={
        isAssignMode && assignSourceInnerTileId === parentPoolInnerTile?.id
      }
      titleOverride="Select tile"
      headerRightSlot={
        isAssignMode && assignSourceInnerTileId === parentPoolInnerTile?.id ? (
          <AssignTilesToolbar
            onSave={handleSaveAssign}
            isSaving={isAssigning}
          />
        ) : !showFiltersAndActions ? (
          <></>
        ) : undefined
      }
      onCreateTile={
        isAssignMode || !parentPoolInnerTile
          ? undefined
          : () => handleCreateTile(parentPoolInnerTile.id)
      }
      onAssignTiles={
        audienceInnerTiles.length > 0 && !isAssignMode && parentPoolInnerTile
          ? () => handleEnterAssignMode(parentPoolInnerTile.id)
          : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          gap: "26px 18px",
          flexDirection: "row",
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        {frames.map(({ frame, variantPreviews }) => (
          <FrameCardLink
            key={frame.id}
            to="/dragon-tiles/$siteId/$parentTile/tile/$frameId"
            params={{
              siteId,
              parentTile: dragonTileId,
              frameId: frame.id,
            }}
            search={(prev) => prev}
            frame={frame}
            siteId={siteId}
            variantPreviews={variantPreviews}
          />
        ))}
      </Box>

      {totalPages > 1 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <TilesPagination
            count={totalPages}
            page={clampedPage}
            onChange={(_, newPage) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  page: newPage,
                }),
              });
            }}
          />
        </Box>
      )}
    </TileSection>
  );

  const renderAudienceTileSection = (
    innerTile: (typeof audienceInnerTiles)[number],
  ) => {
    const isSource = isAssignMode && assignSourceInnerTileId === innerTile.id;
    return (
      <AudienceTileSection
        key={innerTile.id}
        siteId={siteId}
        dragonTileId={dragonTileId}
        innerTile={innerTile}
        filters={frameFilters}
        imageFirst
        hideActions={!showFiltersAndActions}
        onCreateTile={handleCreateTile}
        onAssignTiles={handleEnterAssignMode}
        onAssignSave={isSource ? handleSaveAssign : undefined}
        isAssignSaving={isSource ? isAssigning : false}
        onRemoveFromAudience={isSource ? handleRemoveFromAudience : undefined}
        renderFrameCard={({ frame, variantPreviews }) => (
          <FrameCardLink
            key={frame.id}
            to="/dragon-tiles/$siteId/$parentTile/tile/$frameId"
            params={{
              siteId,
              parentTile: dragonTileId,
              frameId: frame.id,
            }}
            search={(prev) => prev}
            frame={frame}
            siteId={siteId}
            variantPreviews={variantPreviews}
          />
        )}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <OrionPageHeader
          paddingY={6}
          sx={{ backgroundColor: "background.paper-elevation-1" }}
          breadcrumbs={[
            {
              label: "Dragon Tiles",
              to: "/dragon-tiles/$siteId",
              params: { siteId },
            },
          ]}
          title={getDisplayName(dragonTile)}
          description={
            parentTileDescriptionHash[dragonTile.slug]?.tileDescription || ""
          }
          actions={
            <DragonTileActivateSwitch
              isActive={dragonTile.is_active}
              tileStatus={tileStatus}
              attentionMessage={attentionMessage}
              disabled={
                unActivatableDueToListRequirement ||
                !dragonTile.is_activatable ||
                isUpdatingDragonTile
              }
              onChange={() => {
                updateDragonTile(
                  {
                    siteId,
                    dragonTileId,
                    payload: {
                      id: dragonTileId,
                      is_active: !dragonTile.is_active,
                    },
                  },
                  {
                    onSuccess: invalidateDragonTile,
                  },
                );
              }}
              name="activate-tile"
              helperText={activateSwitchHelperText}
            />
          }
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          backgroundColor: "background.paper-elevation-1",
        }}
      >
        <LeftSidebar>
          <Box
            onClick={clearSelection}
            sx={{ cursor: "pointer" }}
            data-testid="parent-tile-sidebar-wrapper"
          >
            <ParentTile
              tile={dragonTile}
              siteId={siteId}
              variant="medium"
              isSelected={!hasSelectedAudience}
              framesLoaded
            />
          </Box>
          <AudiencesSidebarSection siteId={siteId} dragonTile={dragonTile} />
        </LeftSidebar>

        {/* Main content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            position: "relative",
          }}
        >
          {/* Main Content - Split Panel Layout */}
          <Grid container sx={{ flex: 1, minHeight: 0, position: "relative" }}>
            {/* Left Panel - Configuration and Tiles List */}
            <Grid
              size={{ xs: 12, md: isFrameRouteActive ? 6 : 12 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowY: "auto",
              }}
            >
              {/* Matches the content column's 42px gutter (Panel, InnerTilesToolbar) */}
              <BroadcastWarmupBanner
                siteId={siteId}
                disableGutters
                sx={{ px: "42px", pt: 7 }}
                description="We're establishing your sending reputation and reaching your most engaged subscribers."
              />
              {isAnalogModeActive ? (
                <AnalogSequencingPanel
                  siteId={siteId}
                  dragonTile={dragonTile}
                  onCreateTile={handleCreateTile}
                />
              ) : (
                <>
                  {shouldShowConfigSection && (
                    <Panel spacing={0}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle2">
                          Configuration Options
                        </Typography>
                        <Typography variant="body1">
                          {
                            parentTileDescriptionHash[dragonTile.slug]
                              ?.userConfigDescription
                          }
                        </Typography>
                        <DragonTileConfigForm
                          dragonTile={dragonTile}
                          lists={lists}
                          siteId={siteId}
                          dragonTileId={dragonTileId}
                          onUpdateSuccess={invalidateDragonTile}
                        />
                      </Stack>
                    </Panel>
                  )}

                  {showFiltersAndActions && (
                    <InnerTilesToolbar
                      search={search.search}
                      medium={search.medium}
                      status={search.status}
                      orderBy={search.order_by}
                      order={search.order}
                      onSearchChange={(v) => updateSearch("search", v)}
                      onMediumChange={(v) => updateSearch("medium", v)}
                      onStatusChange={(v) => updateSearch("status", v)}
                      onOrderByChange={(v) => updateSearch("order_by", v)}
                      onOrderChange={(v) => updateSearch("order", v)}
                    />
                  )}

                  {!hasSelectedAudience && parentPoolSection}

                  {visibleAudienceInnerTiles.map(renderAudienceTileSection)}

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      backgroundColor: (theme) =>
                        theme.palette.background["paper-elevation-1"],
                    }}
                  />
                </>
              )}
            </Grid>

            {/* Right Panel - Tile Editor Outlet */}
            <AnimatePresence mode="wait">
              {isFrameRouteActive && (
                <Grid
                  size={{ xs: 12, md: 6 }}
                  component={motion.div}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.2 }}
                  sx={{
                    borderLeft: { xs: "none", md: "1px solid #E6DEEC" },
                    borderTop: { xs: "1px solid #E6DEEC", md: "none" },
                    height: "100%",
                    overflow: "visible",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Outlet />
                </Grid>
              )}
            </AnimatePresence>
          </Grid>
        </Box>
      </Box>

      <CreateFrameModal
        open={createFrameRequest !== null}
        onClose={() => setCreateFrameRequest(null)}
        siteId={siteId}
        parentTileId={dragonTileId}
        innerTileId={createFrameRequest?.innerTileId ?? ""}
        forcedLayoutType={createFrameRequest?.forcedLayoutType ?? null}
      />
      <MoveOrCopyConfirmDialog onConfirm={handleConfirmMoveOrCopy} />
    </Box>
  );
}
